// A small centred question with a plain answer: restore, discard, merge, wipe, restart. The request runs when the
// confirm button is pressed; a refusal is said in the api's words inside the dialog, and the dialog stays open.

import { Button, Dialog } from '@clavian/ui-web';
import { useState, type ReactNode } from 'react';

import { problemDetail } from '../api/problems';
import { Notice } from '../ui';

export interface ConfirmDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly title: ReactNode;
  readonly description?: ReactNode;
  readonly confirmLabel: string;
  readonly cancelLabel: string;
  readonly tone?: 'primary' | 'danger';
  /** Runs the request; a rejection is shown. Resolves to close the dialog. */
  readonly onConfirm: () => Promise<unknown>;
  readonly failedText: string;
  readonly children?: ReactNode;
  readonly size?: 'sm' | 'md';
  /** The action cannot be taken yet (the dialog's body says why). */
  readonly confirmDisabled?: boolean;
}

export function ConfirmDialog({
  open,
  onClose,
  title,
  description,
  confirmLabel,
  cancelLabel,
  tone = 'primary',
  onConfirm,
  failedText,
  children,
  size = 'sm',
  confirmDisabled = false,
}: ConfirmDialogProps): ReactNode {
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | undefined>(undefined);

  function close(): void {
    setFailure(undefined);
    onClose();
  }

  async function confirm(): Promise<void> {
    setFailure(undefined);
    setBusy(true);
    try {
      await onConfirm();
      setBusy(false);
      onClose();
    } catch (caught) {
      setBusy(false);
      setFailure(problemDetail(caught) ?? failedText);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? undefined : close())} size={size} title={title} {...(description === undefined ? {} : { description })}>
      <div className="console-form">
        {children}
        {failure === undefined ? null : (
          <Notice tone="danger" role="alert">
            {failure}
          </Notice>
        )}
        <div className="console-form__actions">
          <Button variant="ghost" onClick={close}>
            {cancelLabel}
          </Button>
          <Button variant={tone === 'danger' ? 'danger' : 'primary'} busy={busy} disabled={confirmDisabled} onClick={() => void confirm()}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
